import React from 'react';
import propTypes2 from 'prop-types';

import { camelize } from '../utils/String';
import { encodeSvg } from '../../../../../utils';

const evtNames = [
    'click',
    'dblclick',
    'dragend',
    'mousedown',
    'mouseout',
    'mouseover',
    'mouseup',
    'recenter',
];

const wrappedPromise = function() {
    const wrappedPromise = {},
        promise = new Promise(function(resolve, reject) {
            wrappedPromise.resolve = resolve;
            wrappedPromise.reject = reject;
        });
    wrappedPromise.then = promise.then.bind(promise);
    wrappedPromise.catch = promise.catch.bind(promise);
    wrappedPromise.promise = promise;

    return wrappedPromise;
};

const getIconMarker = markerColor => (
    <svg width='24' height='34' fill='none' stroke='null' xmlns='http://www.w3.org/2000/svg'>
        <ellipse ry='5.64011' rx='6.06702' id='svg_2' cy='12.21365' cx='11.85851' fill='#fff' />
        <path
            fill={markerColor}
            d='m12.64243,33.51399c-0.12419,0.19497 -0.41399,0.35097 -0.66237,0.35097c-0.2898,0 -0.53818,-0.156 -0.66237,-0.35097l-9.72839,-16.61291c-0.99354,-1.59885 -1.53171,-3.50972 -1.53171,-5.4986c0,-6.20058 5.34027,-11.23124 11.92246,-11.23124c6.58221,0 11.92246,5.03066 11.92246,11.23124c0,1.98889 -0.57959,3.89975 -1.5317,5.4986l-9.72839,16.61291zm-5.13345,-22.11136c0,2.33981 1.9871,4.21171 4.47092,4.21171c2.48387,0 4.47092,-1.8719 4.47092,-4.21171c0,-2.33986 -1.98705,-4.21171 -4.47092,-4.21171c-2.48381,0 -4.47092,1.87185 -4.47092,4.21171z'
            clipRule='evenodd'
            fillRule='evenodd'
        />
    </svg>
);

const getIconLocationMarker = markerColor => (
    <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
        <rect stroke="null" x="0" y="0" fill="none" rx="2" height="40" width="40"/>
        <path stroke="null" fill={markerColor}
              d="m20.05275,0.42622a11.45178,10.63616 0 0 0 -11.45178,10.63616c0,7.59776 11.45178,19.49962 11.45178,19.49962s11.45178,-11.90186 11.45178,-19.49962a11.45178,10.63616 0 0 0 -11.45178,-10.63616zm0,3.54539a7.63452,7.09077 0 0 1 7.63452,7.09077c0,3.56311 -3.97186,9.50164 -7.63452,13.9316c-3.6703,-4.42287 -7.63452,-10.35785 -7.63452,-13.9316a7.63452,7.09077 0 0 1 7.63452,-7.09077zm0,3.54539a3.81726,3.54539 0 1 0 3.81726,3.54539a3.81726,3.54539 0 0 0 -3.81726,-3.54539zm-8.62701,18.49983c-6.20114,1.17707 -10.45929,3.55602 -10.45929,6.31788c0,3.91588 8.54494,7.09077 19.0863,7.09077s19.0863,-3.17489 19.0863,-7.09077c0,-2.76186 -4.25815,-5.14081 -10.45929,-6.31788c-0.8627,1.17175 -1.70822,2.246 -2.48122,3.19085c5.21438,0.73389 8.30063,2.26905 9.03164,3.13058c-0.98294,1.15934 -6.20877,3.54539 -15.16788,3.54539l0,0l0,0c-8.9801,-0.00355 -14.19639,-2.38782 -15.18124,-3.54893c0.73101,-0.8633 3.81726,-2.402 9.03164,-3.13412c-0.77872,-0.94307 -1.62424,-2.00669 -2.48694,-3.18376z"
        />
    </svg>
);

export class Marker extends React.Component {
    componentDidMount() {
        this.markerPromise = wrappedPromise();
        this.renderMarker();
    }

    componentDidUpdate(prevProps) {
        const { position, map, icon, colorCustomMarker, circle } = this.props;
        const positionsUnchanged = JSON.stringify(position) === JSON.stringify(prevProps.position);

        if (
            !positionsUnchanged && ((map !== prevProps.map) || (position !== prevProps.position) || (icon !== prevProps.icon)) ||
            positionsUnchanged && colorCustomMarker !== prevProps.colorCustomMarker ||
            circle && circle !== prevProps.circle
        ) {
            if (this.marker) {
                this.marker.setMap(null);
            }
            this.renderMarker();
        } else if (map !== prevProps.map) {
            // first mount
            this.renderMarker();
        }
    }

    componentWillUnmount() {
        if (this.marker) {
            this.marker.setMap(null);
        }
    }

    renderMarker() {
        const {
            map,
            google,
            position,
            mapCenter,
            colorCustomMarker,
            useCustomMarker,
            useLocationMarker,
            icon,
            label,
            draggable,
            idx,
            title,
            circle,
            optimized,
            ...props
        } = this.props;

        if (!google) {
            return null;
        }

        let pos = position || mapCenter;

        if (!(pos instanceof google.maps.LatLng)) {
            pos = new google.maps.LatLng(pos.lat, pos.lng);
        }
        const iconMarker = useCustomMarker ? encodeSvg(getIconMarker(colorCustomMarker))
            : useLocationMarker ? encodeSvg(getIconLocationMarker(colorCustomMarker)) : null;

        const pref = {
            map,
            position: pos,
            icon: iconMarker || icon,
            label,
            title,
            zIndex: colorCustomMarker === '#B30200' ? (1000 + idx) : (idx + 10),
            draggable,
            ...props,
        };

        this.marker = new google.maps.Marker(pref);

        if (circle) {
            this.marker.bindTo('position', circle, 'center');
        }

        evtNames.forEach(e => {
            this.marker.addListener(e, this.handleEvent(e));
        });

        this.markerPromise.resolve(this.marker);
    }

    getMarker() {
        return this.markerPromise;
    }

    handleEvent(evt) {
        return (e) => {
            const evtName = `on${camelize(evt)}`;
            if (this.props[evtName]) {
                this.props[evtName](this.props, this.marker, e);
            }
        };
    }

    render() {
        return null;
    }
}

Marker.propTypes2 = {
    position: propTypes2.object,
    map: propTypes2.object,
    idx: propTypes2.number,
    colorCustomMarker: propTypes2.string,
    useCustomMarker: propTypes2.bool,
    useLocationMarker: propTypes2.bool,
    draggable: propTypes2.bool,
};

Marker.defaultProps = {
    name: 'Marker',
    colorCustomMarker: '#B30200',
};
